import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, DatePicker, Button, Space, Divider, Radio, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import dayjs, { Dayjs } from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

interface BookingData {
  id?: string;
  booking_num?: string;
  booking_date?: string;
  carrier_scac: string;
  voyage_id?: string;
  docs_itn?: string;
  cy_cut?: string;
  etd?: string;
  eta?: string;
  pickup_terminal_id?: string;
  dropoff_terminal_id?: string;
  bl_no?: string;
  portcast_bookmark_id?: string;
  portcast_id?: string;
}

interface ShipmentData {
  id: string;
  portDepartureId?: string;
  portDepartureCode?: string;
  portArrivalId?: string;
  portArrivalCode?: string;
  portIntermediateId?: string;
  portIntermediateCode?: string;
  packingNotes?: string;
  sendDocsToId?: string;
}

interface Carrier {
  id: string;
  scac: string;
  name: string;
}

interface Terminal {
  id: string;
  code: string;
  name: string;
  city?: string;
}

interface PortOption {
  id: string;
  code: string;
  name: string;
  country?: string;
}

interface SendDocsToOption {
  id: string;
  sendTo: string;
}

interface BookingManagementModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: (bookingId: string) => void;
  shipmentId?: string;
  editingBooking?: BookingData;
  shipmentData?: ShipmentData;
}

export const BookingManagementModal: React.FC<BookingManagementModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  shipmentId,
  editingBooking,
  shipmentData,
}) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'create' | 'select'>('create');
  const [selectedExistingBooking, setSelectedExistingBooking] = useState<string | undefined>();

  const isEditMode = !!editingBooking;

  const { data: carriersData } = useQuery({
    queryKey: ['carriers'],
    queryFn: async () => {
      return await api.get('/carriers', {
        params: { active: true, limit: 200 }
      }) as any;
    },
  });

  const carrierScac = Form.useWatch('carrier_scac', form);
  const effectiveCarrier = carrierScac || (isEditMode ? editingBooking?.carrier_scac : null);
  
  const { data: voyagesData } = useQuery({
    queryKey: ['voyages', effectiveCarrier, isEditMode, editingBooking?.voyage_id],
    queryFn: async () => {
      const params: any = { limit: 200 };
      if (!isEditMode) {
        params.active = true;
      }
      if (effectiveCarrier && !isEditMode) {
        params.carrier_scac = effectiveCarrier;
      }
      const data = await api.get('/voyages', { params }) as any;
      return data;
    },
    enabled: true,
  });

  const { data: terminalsData } = useQuery({
    queryKey: ['terminals'],
    queryFn: async () => {
      return await api.get('/terminals', {
        params: { active: true, limit: 200 }
      }) as any;
    },
  });

  const { data: portsData } = useQuery({
    queryKey: ['ports'],
    queryFn: async () => {
      return await api.get('/reference/ports') as any;
    },
  });

  const { data: sendDocsToData } = useQuery({
    queryKey: ['send-documents-to'],
    queryFn: async () => {
      return await api.get('/reference/send-documents-to') as any;
    },
  });

  const { data: existingBookingsData } = useQuery({
    queryKey: ['bookings-list'],
    queryFn: async () => {
      return await api.get('/bookings', {
        params: { active: true, limit: 100 }
      }) as any;
    },
    enabled: mode === 'select' && !isEditMode,
  });

  const createBookingMutation = useMutation({
    mutationFn: async (data: BookingData) => {
      return await api.post('/bookings', data) as any;
    },
    onSuccess: (data: any) => {
      message.success('Booking created successfully');
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      
      if (shipmentId) {
        attachBookingToShipment(data.id);
      } else {
        onSuccess(data.id);
      }
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to create booking');
    },
  });

  const updateBookingMutation = useMutation({
    mutationFn: async (data: BookingData) => {
      return await api.patch(`/bookings/${editingBooking?.id}`, data) as any;
    },
    onSuccess: (data: any) => {
      message.success('Booking updated successfully');
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      onSuccess(data.id);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to update booking');
    },
  });

  const attachBookingToShipment = async (bookingId: string) => {
    try {
      await api.patch(`/shipments/${shipmentId}/booking`, { booking_id: bookingId });
      message.success('Booking attached to shipment');
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      onSuccess(bookingId);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to attach booking');
    }
  };

  const updateShipmentFields = async (values: any) => {
    const effectiveShipmentId = shipmentData?.id || shipmentId;
    if (!effectiveShipmentId) return;

    const shipmentUpdate: Record<string, string | undefined> = {};

    if (values.port_departure_id !== undefined) {
      const port = (portsData?.ports as PortOption[] | undefined)?.find((p: PortOption) => p.id === values.port_departure_id);
      if (port) shipmentUpdate.port_departure_code = port.code;
      else if (!values.port_departure_id) shipmentUpdate.port_departure_code = '';
    }

    if (values.port_arrival_id !== undefined) {
      const port = (portsData?.ports as PortOption[] | undefined)?.find((p: PortOption) => p.id === values.port_arrival_id);
      if (port) shipmentUpdate.port_arrival_code = port.code;
      else if (!values.port_arrival_id) shipmentUpdate.port_arrival_code = '';
    }

    if (values.port_intermediate_id !== undefined) {
      const port = (portsData?.ports as PortOption[] | undefined)?.find((p: PortOption) => p.id === values.port_intermediate_id);
      if (port) shipmentUpdate.port_intermediate_code = port.code;
      else if (!values.port_intermediate_id) shipmentUpdate.port_intermediate_code = '';
    }

    if (values.packing_notes !== undefined) {
      shipmentUpdate.packing_notes = values.packing_notes || '';
    }

    if (values.send_docs_to_id !== undefined) {
      shipmentUpdate.send_docs_to_id = values.send_docs_to_id || '';
    }

    if (Object.keys(shipmentUpdate).length > 0) {
      await api.patch(`/shipments/id/${effectiveShipmentId}`, shipmentUpdate);
    }
  };

  const handleSubmit = async () => {
    if (mode === 'select' && selectedExistingBooking) {
      if (shipmentId) {
        await attachBookingToShipment(selectedExistingBooking);
      } else {
        onSuccess(selectedExistingBooking);
      }
      return;
    }

    try {
      const values = await form.validateFields();
      
      const bookingData: BookingData = {
        booking_num: values.booking_num || undefined,
        booking_date: values.booking_date ? values.booking_date.format('YYYY-MM-DD') : undefined,
        carrier_scac: values.carrier_scac || 'ONEY',
        voyage_id: values.voyage_id || undefined,
        docs_itn: values.docs_itn ? values.docs_itn.toISOString() : undefined,
        cy_cut: values.cy_cut ? values.cy_cut.toISOString() : undefined,
        etd: values.etd ? values.etd.format('YYYY-MM-DD') : undefined,
        eta: values.eta ? values.eta.format('YYYY-MM-DD') : undefined,
        pickup_terminal_id: values.pickup_terminal_id || undefined,
        dropoff_terminal_id: values.dropoff_terminal_id || undefined,
        bl_no: values.bl_no || undefined,
      };

      if (isEditMode) {
        try {
          await updateShipmentFields(values);
        } catch (error) {
          console.error('Failed to update shipment fields:', error);
        }
        updateBookingMutation.mutate(bookingData);
      } else {
        createBookingMutation.mutate(bookingData);
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  useEffect(() => {
    if (!visible) {
      return;
    }

    if (editingBooking) {
      form.setFieldsValue({
        booking_num: editingBooking.booking_num,
        booking_date: editingBooking.booking_date ? dayjs(editingBooking.booking_date) : undefined,
        carrier_scac: editingBooking.carrier_scac,
        voyage_id: editingBooking.voyage_id,
        docs_itn: editingBooking.docs_itn ? dayjs(editingBooking.docs_itn) : undefined,
        cy_cut: editingBooking.cy_cut ? dayjs(editingBooking.cy_cut) : undefined,
        etd: editingBooking.etd ? dayjs(editingBooking.etd) : undefined,
        eta: editingBooking.eta ? dayjs(editingBooking.eta) : undefined,
        pickup_terminal_id: editingBooking.pickup_terminal_id,
        dropoff_terminal_id: editingBooking.dropoff_terminal_id,
        bl_no: editingBooking.bl_no,
        port_departure_id: shipmentData?.portDepartureId || undefined,
        port_arrival_id: shipmentData?.portArrivalId || undefined,
        port_intermediate_id: shipmentData?.portIntermediateId || undefined,
        packing_notes: shipmentData?.packingNotes || undefined,
        send_docs_to_id: shipmentData?.sendDocsToId || undefined,
      });
    } else {
      form.resetFields();
      setMode('create');
      setSelectedExistingBooking(undefined);
    }
  }, [visible, editingBooking, shipmentData, form]);

  const handleCancel = () => {
    if (!isEditMode) {
      form.resetFields();
      setMode('create');
      setSelectedExistingBooking(undefined);
    }
    onCancel();
  };

  const disabledDate = (current: Dayjs) => {
    return current && current < dayjs().startOf('day');
  };

  const ports: PortOption[] = portsData?.ports || [];
  const sendDocsToOptions: SendDocsToOption[] = sendDocsToData?.send_documents_to || [];

  return (
    <Modal
      title={isEditMode ? 'Edit Booking & Shipment' : 'Booking Management'}
      open={visible}
      onCancel={handleCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={createBookingMutation.isPending || updateBookingMutation.isPending}
          onClick={handleSubmit}
        >
          {isEditMode ? 'Update Booking' : mode === 'select' ? 'Attach Booking' : 'Create Booking'}
        </Button>,
      ]}
    >
      {!isEditMode && (
        <>
          <Radio.Group
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            style={{ marginBottom: 24 }}
          >
            <Radio.Button value="create">Create New Booking</Radio.Button>
            <Radio.Button value="select">Select Existing Booking</Radio.Button>
          </Radio.Group>
          <Divider />
        </>
      )}

      {mode === 'select' && !isEditMode ? (
        <Form layout="vertical">
          <Form.Item label="Select Booking" required>
            <Select
              placeholder="Search bookings..."
              showSearch
              value={selectedExistingBooking}
              onChange={setSelectedExistingBooking}
              filterOption={(input, option) => {
                const children = option?.children as string | undefined;
                return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
              }}
            >
              {existingBookingsData?.bookings?.map((booking: any) => (
                <Option key={booking.id} value={booking.id}>
                  {booking.booking_num} - {booking.carrier_text} - {booking.voyage_text}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item
            label="Booking Number"
            name="booking_num"
            rules={[{ required: false }]}
          >
            <Input placeholder="Enter booking number" />
          </Form.Item>

          <Form.Item label="Booking Date" name="booking_date">
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item
            label="Carrier"
            name="carrier_scac"
          >
            <Select
              placeholder="Select carrier"
              showSearch
              allowClear
              filterOption={(input, option) => {
                const children = option?.children as string | undefined;
                return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
              }}
            >
              {carriersData?.carriers?.map((carrier: Carrier) => (
                <Option key={carrier.scac} value={carrier.scac}>
                  {carrier.name} ({carrier.scac})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Voyage"
            name="voyage_id"
          >
            <Select
              placeholder="Select voyage"
              showSearch
              allowClear
              disabled={!carrierScac}
              filterOption={(input, option) => {
                const children = option?.children as string | undefined;
                return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
              }}
            >
              {voyagesData?.voyages?.map((voyage: any) => (
                <Option key={voyage.id} value={voyage.id}>
                  {voyage.vessel_name && voyage.voyage_number 
                    ? `${voyage.vessel_name} / ${voyage.voyage_number}`
                    : voyage.voyage_number || voyage.id}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {isEditMode && (
            <>
              <Divider>Ports</Divider>

              <Form.Item label="Port of Loading" name="port_departure_id">
                <Select
                  placeholder="Select port of loading"
                  showSearch
                  allowClear
                  filterOption={(input, option) => {
                    const children = option?.children as string | undefined;
                    return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
                  }}
                >
                  {ports.map((port) => (
                    <Option key={port.id} value={port.id}>
                      {port.name} ({port.code})
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Port of Delivery" name="port_arrival_id">
                <Select
                  placeholder="Select port of delivery"
                  showSearch
                  allowClear
                  filterOption={(input, option) => {
                    const children = option?.children as string | undefined;
                    return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
                  }}
                >
                  {ports.map((port) => (
                    <Option key={port.id} value={port.id}>
                      {port.name} ({port.code})
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Transshipment Port" name="port_intermediate_id">
                <Select
                  placeholder="Select transshipment port (optional)"
                  showSearch
                  allowClear
                  filterOption={(input, option) => {
                    const children = option?.children as string | undefined;
                    return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
                  }}
                >
                  {ports.map((port) => (
                    <Option key={port.id} value={port.id}>
                      {port.name} ({port.code})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}

          <Divider>Dates & Cutoffs</Divider>

          <Space.Compact block>
            <Form.Item label="Docs/ITN Cutoff" name="docs_itn" style={{ width: '50%', marginRight: 8 }}>
              <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
            </Form.Item>

            <Form.Item label="CY Cut" name="cy_cut" style={{ width: '50%' }}>
              <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
            </Form.Item>
          </Space.Compact>

          <Space.Compact block>
            <Form.Item label="ETD" name="etd" style={{ width: '50%', marginRight: 8 }}>
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>

            <Form.Item label="ETA" name="eta" style={{ width: '50%' }}>
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>
          </Space.Compact>

          <Divider>Terminals</Divider>

          <Form.Item label="Container Pickup Terminal" name="pickup_terminal_id">
            <Select
              placeholder="Select pickup terminal"
              showSearch
              allowClear
              filterOption={(input, option) => {
                const children = option?.children as string | undefined;
                return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
              }}
            >
              {terminalsData?.terminals?.map((terminal: Terminal) => (
                <Option key={terminal.id} value={terminal.id}>
                  {terminal.name} ({terminal.code}) {terminal.city ? `- ${terminal.city}` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Container Dropoff Terminal" name="dropoff_terminal_id">
            <Select
              placeholder="Select dropoff terminal"
              showSearch
              allowClear
              filterOption={(input, option) => {
                const children = option?.children as string | undefined;
                return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
              }}
            >
              {terminalsData?.terminals?.map((terminal: Terminal) => (
                <Option key={terminal.id} value={terminal.id}>
                  {terminal.name} ({terminal.code}) {terminal.city ? `- ${terminal.city}` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {isEditMode && (
            <>
              <Divider>Shipment Details</Divider>

              <Form.Item label="Special Instructions" name="packing_notes">
                <TextArea rows={3} placeholder="Enter special instructions / packing notes" />
              </Form.Item>

              <Form.Item label="Send Docs To" name="send_docs_to_id">
                <Select
                  placeholder="Select send docs to"
                  showSearch
                  allowClear
                  filterOption={(input, option) => {
                    const children = option?.children as string | undefined;
                    return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
                  }}
                >
                  {sendDocsToOptions.map((opt) => (
                    <Option key={opt.id} value={opt.id}>
                      {opt.sendTo}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}

          <Divider>Tracking (Optional)</Divider>

          <Form.Item label="Bill of Lading Number" name="bl_no">
            <Input placeholder="Enter B/L number" />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default BookingManagementModal;
